/**
 * DungeonRunScene (E4.6.S3 slice 2e, AD-705) — draws the networked run the
 * player is in, where the server simulates it.
 *
 * THE INSTANCE FRAME. The server's instance zone is the plan's layout in
 * plan-local metres (`dungeonInstanceZoneDef`: bounds = the layout's bounds
 * plus a margin, walls = `buildLayoutCollision(plan)`), and every snapshot of
 * the run positions bodies in that frame. So the interior is built at the
 * ORIGIN — not the techdemo explorer's +1600 offset — and the entities the
 * snapshot stream already draws stand inside it with no transform at all.
 *
 * THE WORLD IS HIDDEN, NOT THE REST NARROWED. The explorer's active slot
 * (`scene/activeSlot.ts`) narrows the camera to one complete subtree; here the
 * bodies, health bars and telegraphs live as their own top-level scene
 * children and keep arriving mid-run (an add spawning, a rig finishing its
 * load), so a slot would have to adopt each one. Instead the open world's
 * top-level roots carry the world-content tag (`scene/worldContent.ts`) and are
 * hidden each frame while the run is drawn: a root added late (an NPC whose
 * async build lands mid-run, a world rebuilt by a late content pack) is caught
 * on the next frame, before it is rendered. Release restores each root's own
 * `visible` and touches nothing that left the scene meanwhile.
 *
 * Interiors are never cached: one per run, disposed on release. Building is
 * async (pack loads + an off-frame shader compile); a release that lands first
 * discards the build. Presentation-only; frontend-local (AD-122).
 */

import * as THREE from 'three';

import type { DungeonCatalog, DungeonPlan } from '@great-cloud/procedural-dungeons';

import { disposeObject3D } from '../assets/gpuOwnership.js';
import { isWorldContent } from '../scene/worldContent.js';
import { runTeardown } from '../teardown.js';
import { buildDungeonInterior, type LoaderFor } from './interior.js';
import type { InteriorPrecompile } from './explorer.js';

/** The instance frame: plan-local metres, i.e. the scene origin. */
const INSTANCE_ORIGIN = new THREE.Vector3(0, 0, 0);

/**
 * Hide the scene's tagged top-level children, remembering each one's own
 * `visible` the first time it is seen. Called every frame while a run is drawn.
 */
export function hideWorldContent(scene: THREE.Object3D, saved: Map<THREE.Object3D, boolean>): void {
  for (const child of scene.children) {
    if (!isWorldContent(child)) continue;
    if (!saved.has(child)) saved.set(child, child.visible);
    child.visible = false;
  }
}

/** Restore what `hideWorldContent` hid — only for roots still in `scene`. */
export function restoreWorldContent(
  scene: THREE.Object3D,
  saved: Map<THREE.Object3D, boolean>,
): void {
  for (const [object, visible] of saved) {
    if (object.parent === scene) object.visible = visible;
  }
  saved.clear();
}

export class DungeonRunScene {
  private group: THREE.Group | null = null;
  private readonly hidden = new Map<THREE.Object3D, boolean>();
  /** Bumped by every show/release so a slow build can tell it was superseded. */
  private generation = 0;
  private drawing = false;

  constructor(
    private readonly scene: THREE.Scene,
    private readonly camera: THREE.Camera,
    private readonly loaderFor: LoaderFor,
    private readonly catalog: DungeonCatalog,
    private readonly precompile: InteriorPrecompile | null = null,
    private readonly warn: (message: string) => void = () => {},
  ) {}

  /** True from `show()` until `release()` — the world is hidden throughout. */
  get active(): boolean {
    return this.drawing;
  }

  /** The interior group once built (null while building or outside a run). */
  get interior(): THREE.Group | null {
    return this.group;
  }

  /**
   * Draw `plan` at the instance frame. The world is hidden at once (the body is
   * already in the instance); the interior joins when its build settles.
   */
  async show(plan: DungeonPlan): Promise<void> {
    this.release();
    const generation = ++this.generation;
    this.drawing = true;
    hideWorldContent(this.scene, this.hidden);
    const theme = this.catalog.themes.find((entry) => entry.id === plan.themeId);
    if (!theme) {
      this.warn(`no theme ${plan.themeId} in the bundled catalogue: the run draws no walls`);
      return;
    }
    let built: Awaited<ReturnType<typeof buildDungeonInterior>>;
    try {
      built = await buildDungeonInterior(
        plan,
        theme,
        this.catalog,
        this.loaderFor,
        INSTANCE_ORIGIN.clone(),
        // The server's enemies are entities; the orbit camera rides above the walls.
        { encounterProxies: false, ceilings: false },
      );
    } catch (error) {
      if (generation === this.generation) this.warn(`interior build failed: ${String(error)}`);
      return;
    }
    if (generation !== this.generation) {
      disposeGroup(built.group);
      return;
    }
    // Compile off the frame first (dungeon design §12d — the first interior frame
    // once froze > 60 s compiling synchronously); a failure only costs that stall.
    if (this.precompile) {
      await this.precompile(built.group, this.camera, this.scene).catch(() => undefined);
      if (generation !== this.generation) {
        disposeGroup(built.group);
        return;
      }
    }
    built.group.name = `dungeon-run:${plan.id}`;
    this.scene.add(built.group);
    this.group = built.group;
  }

  /** Per frame, before render: keep every world root hidden (late arrivals included). */
  frame(): void {
    if (this.drawing) hideWorldContent(this.scene, this.hidden);
  }

  /** The run is over (or superseded): dispose the interior, give the world back. Idempotent. */
  release(): void {
    this.generation += 1;
    if (!this.drawing && !this.group) return;
    this.drawing = false;
    runTeardown([
      () => restoreWorldContent(this.scene, this.hidden),
      () => {
        const group = this.group;
        this.group = null;
        if (!group) return;
        this.scene.remove(group);
        disposeGroup(group);
      },
    ]);
  }
}

/** Free the interior's OWN resources; its kit pieces are clones of the pack cache's templates (H-1349). */
function disposeGroup(group: THREE.Object3D): void {
  disposeObject3D(group);
}
